import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { DateTime } from "luxon";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useSnackbar } from "notistack";

import { useWidget } from "/imports/state/WidgetContext";
import { useAuth } from "/imports/state/AuthContext";
import { useBackTo } from "/imports/ui/components/discovery";
import { useUrlFilters, dateUtils } from "/imports/libs/url";
import api from "/imports/api/client";
import useFetch from "/imports/hooks/useFetch";
import useClientPreferences from "/imports/hooks/useClientPreferences";
import usePackageOffers from "/imports/hooks/usePackageOffers";
import { buildWeekDates, resolvePreferredLocationId } from "/imports/ui/components/booking";
import { offerRowKey } from "../PackagesView/packagesHelpers";
import { AGE_OPTIONS, joinedWaitlistMessage } from "./classHelpers";

export default function useClassBooking() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { slug, services, sports, locations, selectedLocation, addToCart, currency } = useWidget();
  const goBack = useBackTo(`/${slug}`);
  const { isAuthenticated, activeClientId } = useAuth();

  const { filters, setFilter, setFilters } = useUrlFilters();

  const [viewMode, setViewMode] = useState("day"); // desktop only: 'day' | 'week'
  const [weekStart, setWeekStart] = useState(() => dateUtils.weekStart(new Date())); // ISO Monday
  const [activeSheet, setActiveSheet] = useState(null); // mobile filter bottom sheet
  const [optionsAnchor, setOptionsAnchor] = useState(null); // week-grid class popover anchor
  const [optionsClass, setOptionsClass] = useState(null); // class shown in the popover
  // A waitlist join/leave changes one card's block, not the week: the change is
  // patched in locally and dropped once the week is fetched fresh. Refetching
  // the whole timetable for it reloaded every card the client was looking at.
  const [waitlistPatches, setWaitlistPatches] = useState({});
  // A second tap while a join is still in flight is ignored.
  const joiningWaitlist = useRef(new Set());

  const classServices = useMemo(
    () => (services || []).filter((s) => s.categoryType === "category-service-class" && !s.isStandaloneClassCategory),
    [services]
  );

  // Standalone class categories (Events…) have their own page, so the Group
  // Classes timetable leaves their sessions out.
  const excludeCategoryIds = useMemo(
    () => [...new Set((services || []).filter((s) => s.isStandaloneClassCategory).map((s) => s.categoryId))].join(","),
    [services]
  );

  const availableSports = useMemo(() => {
    const sportIds = new Set(classServices.map((s) => s.sportId).filter(Boolean));
    return (sports || []).filter((s) => sportIds.has(s._id));
  }, [classServices, sports]);

  const sportFilteredServices = useMemo(() => {
    if (!filters.sport) return classServices;
    return classServices.filter((s) => s.sportId === filters.sport);
  }, [classServices, filters.sport]);

  // Locations to offer in the filter. With "All Classes" (no specific class)
  // every location is relevant; once a class is picked, scope to the locations
  // it's offered at (fall back to all for a class that pins none) — picking any
  // other location would just yield no classes.
  const serviceLocations = useMemo(() => {
    const svc = filters.serviceId ? sportFilteredServices.find((s) => s._id === filters.serviceId) : null;
    const ids = svc?.locationIds || [];
    if (!svc || !ids.length) return locations || [];
    return (locations || []).filter((l) => ids.includes(l._id));
  }, [sportFilteredServices, filters.serviceId, locations]);

  // Once a location is chosen, the Class picker should only list classes held
  // there — the timetable is already location-scoped, this keeps the picker in
  // sync instead of offering classes that produce no sessions at that location.
  const locationFilteredServices = useMemo(() => {
    if (!filters.locationId) return sportFilteredServices;
    return sportFilteredServices.filter((s) => (s.locationIds || []).includes(filters.locationId));
  }, [sportFilteredServices, filters.locationId]);

  const locationId = filters.locationId || selectedLocation?._id;
  const weekDates = useMemo(() => buildWeekDates(weekStart), [weekStart]);
  const dayViewActive = isMobile || viewMode === "day";

  // Broad fetch (default 2-week window) — populates the trainer list and the
  // client's own levels so both stay stable as the user navigates between weeks.
  const { data: broadTimetable } = useFetch(
    () => api.getClassTimetable(slug, { sport: filters.sport, locationId, excludeCategoryIds }),
    [slug, filters.sport, locationId, excludeCategoryIds, activeClientId],
    { skip: !slug }
  );

  // Week fetch — one ranged call covers the whole visible week.
  const {
    data: timetable,
    loading,
    error
  } = useFetch(
    () =>
      api.getClassTimetable(slug, {
        dateStart: weekDates[0],
        dateEnd: weekDates[6],
        sport: filters.sport,
        trainerId: filters.trainerId,
        locationId,
        excludeCategoryIds,
        withOffers: true
      }),
    [slug, weekStart, filters.sport, filters.trainerId, locationId, excludeCategoryIds, activeClientId],
    { skip: !slug }
  );

  useEffect(() => setWaitlistPatches({}), [timetable]);
  const allClasses = useMemo(
    () =>
      (timetable?.classes || []).map((c) => (waitlistPatches[c._id] ? { ...c, waitlist: { ...(c.waitlist || {}), ...waitlistPatches[c._id] } } : c)),
    [timetable, waitlistPatches]
  );
  const patchWaitlist = (classId, patch) => setWaitlistPatches((p) => ({ ...p, [classId]: { ...(p[classId] || {}), ...patch } }));

  // A package with sessions left outlives the club's booking window, and the
  // sessions it covers stay listed for as long as it does. Nothing on the page
  // says so, and a client who has already hit the end of the calendar once
  // won't try again — so say it. The broad fetch answers it too, so the line
  // doesn't blink out on a week holding none of the covered classes.
  const ownedPackHorizon = timetable?.ownedPackHorizon || broadTimetable?.ownedPackHorizon || null;

  // When that credit hasn't opened yet — a pack anchored to a first session in
  // October, bought in August — the line above turns into the way there. The
  // server sends this only while the date is still ahead, so it never scrolls
  // anyone backwards into a week they can no longer book.
  const ownedPackStart = timetable?.ownedPackStart || broadTimetable?.ownedPackStart || null;

  // How many packs are behind that reach. Two packs with different windows have
  // no single "runs X – Y" to quote — the copy switches to the plural, which
  // claims only what is true of the set: the last date any of them covers.
  const ownedPackCount = timetable?.ownedPackCount || broadTimetable?.ownedPackCount || 0;

  // True when that reach is the one-year ceiling rather than a real expiry —
  // clubs do sell packs with no end date, and quoting one would invent a
  // deadline the client does not have.
  const ownedPackCapped = timetable?.ownedPackCapped || broadTimetable?.ownedPackCapped || false;
  // The week the pack opens in usually starts days BEFORE the pack does —
  // 1 Oct is a Thursday, so the jump lands on a week beginning Sun 27 Sep and,
  // left alone, opens on the 27th: three days the pack cannot pay for. Remember
  // the day that was asked for so the view can settle on it once the week loads.
  const [pendingDay, setPendingDay] = useState(null);
  const goToPackStart = () => {
    if (!ownedPackStart) return;
    setWeekStart(dateUtils.weekStart(new Date(`${ownedPackStart}T00:00:00`)));
    setPendingDay(ownedPackStart);
  };
  const clearPendingDay = () => setPendingDay(null);

  const availableTrainers = useMemo(() => {
    const seen = new Set();
    const result = [];
    for (const cls of broadTimetable?.classes || []) {
      if (cls.trainer && !seen.has(cls.trainer._id)) {
        seen.add(cls.trainer._id);
        result.push(cls.trainer);
      }
    }
    return result;
  }, [broadTimetable]);

  // Fixed audience set so the filter is always offered. "No preference" — no
  // age param at all — is the default: the audiences are exclusive tags, so
  // opening on any one of them, "Everyone" included, hides every class the club
  // tagged for another audience.
  const availableAges = AGE_OPTIONS;

  // "Use my preferences" — snap the filters to the active client's preferred
  // class / sport / trainer when they're offered here. A preferred class pins
  // its own sport; otherwise the preferred sport is set and Class stays "All".
  const preferences = useClientPreferences(slug);
  const preferredClassService = useMemo(
    () => preferences.services.find((ps) => classServices.some((s) => s._id === ps.serviceId)) || null,
    [preferences.services, classServices]
  );
  const preferredSport = useMemo(
    () => preferences.sports.find((ps) => availableSports.some((s) => s._id === ps.sportId)) || null,
    [preferences.sports, availableSports]
  );
  const preferredTrainer = useMemo(
    () => preferences.trainers.find((pt) => availableTrainers.some((t) => t._id === pt.trainerId)) || null,
    [preferences.trainers, availableTrainers]
  );
  // The preferred location to apply, restricted to what the currently selected
  // class is offered at — drives whether the button shows on location alone.
  const preferredLocationId = useMemo(
    () => resolvePreferredLocationId(preferences.locations, serviceLocations),
    [preferences.locations, serviceLocations]
  );
  const canApplyPreferences = isAuthenticated && (preferredClassService || preferredSport || preferredTrainer || preferredLocationId);

  // Location is resolved against the class being applied (all view locations when
  // no class is pinned) and only set when bookable there, so the coherence effect
  // never clears it.
  const applyPreferences = () => {
    const svc = preferredClassService ? classServices.find((s) => s._id === preferredClassService.serviceId) : null;
    const sportId = svc?.sportId || preferredSport?.sportId || filters.sport || null;
    const targetLocations = svc?.locationIds?.length ? (locations || []).filter((l) => svc.locationIds.includes(l._id)) : locations || [];
    const locationId = resolvePreferredLocationId(preferences.locations, targetLocations);
    const next = { sport: sportId, serviceId: svc?._id || null, trainerId: preferredTrainer?.trainerId || null };
    if (locationId) next.locationId = locationId;
    setFilters(next);
  };

  // The acting client's assessed levels, one row per sport. A club can run 40+
  // levels, so the filter is a two-way switch — all levels, or the client's own
  // — instead of the full ladder. Read from the week payload that carries the
  // classes being rendered, so the level scope resolves in the same tick they
  // do; the broad fetch covers the gap while a new week is in flight.
  //
  // A client can hold more than one level in the same sport, so a row carries
  // `levelNames`; the server keeps the singular `levelName` as a joined string
  // for older builds.
  const myLevels = timetable?.filters?.myLevels || broadTimetable?.filters?.myLevels || [];

  // "Level 3 – Beginner 2" or, for the rare two-level client, "Level 3 –
  // Beginner 2 + Level 4 – Beginner 3" — naming both is what tells them their
  // timetable is showing the classes of both.
  const namesOf = (lvl) => (lvl?.levelNames?.length ? lvl.levelNames : [lvl?.levelName].filter(Boolean));
  const labelOf = (lvl) => namesOf(lvl).join(" + ");
  const isMulti = (lvl) => namesOf(lvl).length > 1;
  // The level names don't fit a closed filter box — once a client holds two they
  // are cut off mid-name. The box says "My level(s)"; the open list names them.
  const myLevelLabel = (lvl) => (isMulti(lvl) ? "My levels" : "My level");

  // Which of a multi-sport client's levels to open on. Landing them somewhere
  // empty is the failure to avoid, so the sports that actually have a class
  // they can join go first; their preferred sport breaks the tie, then the
  // club's own sport order. A Padel level with nothing bookable never wins over
  // a Tennis level with a class on Friday.
  const sportsWithJoinableClasses = useMemo(() => {
    const source = broadTimetable?.classes || allClasses;
    return new Set(source.filter((c) => c.sport && c.eligibility?.eligible !== false).map((c) => c.sport));
  }, [broadTimetable, allClasses]);

  const defaultLevelSportId = useMemo(() => {
    if (!myLevels.length) return null;
    const joinable = myLevels.filter((l) => sportsWithJoinableClasses.has(l.sportId));
    const pool = joinable.length ? joinable : myLevels;
    return (pool.find((l) => l.sportId === preferredSport?.sportId) || pool[0]).sportId;
  }, [myLevels, sportsWithJoinableClasses, preferredSport]);

  // The level "my level" stands for right now. The sport filter wins when set;
  // otherwise it is the sport named in the URL — a client assessed in several
  // sports (Tennis and Padel, say) picks which of their levels to browse — and
  // failing that the default above.
  const urlLevelSportId = filters.level?.startsWith("mine:") ? filters.level.slice("mine:".length) : null;
  const activeLevel = useMemo(() => {
    const sportId = filters.sport || (myLevels.some((l) => l.sportId === urlLevelSportId) ? urlLevelSportId : defaultLevelSportId);
    return myLevels.find((l) => l.sportId === sportId) || null;
  }, [myLevels, filters.sport, urlLevelSportId, defaultLevelSportId]);

  // Hidden for signed-out visitors and for anyone with no level on the sport in
  // view. Every class the club runs is level-gated, so an unassessed client
  // filtering by "my level" would only ever empty the timetable — they need an
  // assessment first, which the class cards already prompt for.
  const levelFilterAvailable = isAuthenticated && !!activeLevel;

  // One row per assessed sport while browsing all sports, each naming its sport
  // — the level scopes the timetable to that sport, so "My level" alone would
  // hide the other sports' classes without saying so. Once the Sport filter is
  // set it already names the sport, leaving a single plain "My level" row.
  const levelOptions = useMemo(() => {
    if (!levelFilterAvailable) return [];
    if (filters.sport) return [{ value: `mine:${activeLevel.sportId}`, label: myLevelLabel(activeLevel), sublabel: labelOf(activeLevel) }];
    return myLevels.map((l) => ({ value: `mine:${l.sportId}`, label: `${l.sportName} · ${myLevelLabel(l)}`, sublabel: labelOf(l) }));
  }, [levelFilterAvailable, filters.sport, activeLevel, myLevels]);

  // An assessed client opens on their own level — the classes they can actually
  // join come first, which is the point of the filter. "All Levels" is the
  // explicit opt-out ("all" in the URL, so it survives navigation); an absent
  // param means "not chosen yet" and falls back to the default. Anyone without
  // a level to match against is pinned to "all" whatever the URL says, so a
  // shared level link can't empty their timetable.
  const levelScope = levelFilterAvailable && filters.level !== "all" ? "mine" : "all";
  const levelValue = levelScope === "mine" ? `mine:${activeLevel.sportId}` : null;
  const levelChipLabel = !activeLevel ? "All" : filters.sport ? myLevelLabel(activeLevel) : `${activeLevel.sportName} · ${myLevelLabel(activeLevel)}`;
  const setLevelScope = (val) => setFilter("level", val || "all");

  // Guard against an out-of-range value arriving via the URL.
  useEffect(() => {
    if (filters.age && !availableAges.some((a) => a.value === filters.age)) {
      setFilter("age", null);
    }
  }, [filters.age, setFilter]);

  // Keep the location coherent with the class: lock to the sole location when a
  // single-location class is selected (so its name shows instead of a confusing
  // "All Locations"), otherwise drop a stale location the class doesn't serve.
  useEffect(() => {
    if (serviceLocations.length === 1) {
      if (filters.locationId !== serviceLocations[0]._id) setFilter("locationId", serviceLocations[0]._id);
      return;
    }
    if (filters.locationId && !serviceLocations.some((l) => l._id === filters.locationId)) {
      setFilter("locationId", null);
    }
  }, [serviceLocations, filters.locationId, setFilter]);

  // Sport, location and trainer are filtered by the timetable endpoint; class
  // (serviceId), age & gender and level are narrowed here on the client.
  //
  // "My level" keeps what the client can actually join in that sport: classes
  // whose required levels include theirs, plus any with no level gate — the
  // server already resolved both into eligibility. The sport check is what
  // makes the choice mean something on "All Sports", where a Tennis level must
  // not pull in the Padel classes.
  const otherFiltersMatch = (c) => {
    if (filters.serviceId && c.serviceId !== filters.serviceId) return false;
    if (filters.age && c.age !== filters.age) return false;
    return true;
  };

  const matchesLevel = (c) => levelScope !== "mine" || (c.sport === activeLevel.sportId && !(c.eligibility && !c.eligibility.eligible));

  const visibleClasses = useMemo(
    () => allClasses.filter((c) => otherFiltersMatch(c) && matchesLevel(c)),
    [allClasses, filters.serviceId, filters.age, levelScope, activeLevel]
  );

  // Classes the level scope alone is holding back. Since an assessed client
  // lands on their own level, a week that looks empty may just be a week of
  // classes for other levels — the empty state offers the way out.
  const hiddenByLevel = useMemo(
    () => (levelScope === "mine" ? allClasses.filter((c) => otherFiltersMatch(c) && !matchesLevel(c)).length : 0),
    [allClasses, filters.serviceId, filters.age, levelScope, activeLevel]
  );

  const classesByDate = useMemo(() => {
    const m = new Map();
    for (const c of visibleClasses) {
      if (!m.has(c.date)) m.set(c.date, []);
      m.get(c.date).push(c);
    }
    for (const arr of m.values()) arr.sort((a, b) => a.time.localeCompare(b.time));
    return m;
  }, [visibleClasses]);

  const nonEmptyDays = useMemo(() => weekDates.filter((d) => (classesByDate.get(d) || []).length > 0), [weekDates, classesByDate]);
  const weekHasClasses = nonEmptyDays.length > 0;

  const goPrevWeek = () => setWeekStart(DateTime.fromISO(weekStart).minus({ days: 7 }).toISODate());
  const goNextWeek = () => setWeekStart(DateTime.fromISO(weekStart).plus({ days: 7 }).toISODate());

  // A week left empty by the level default is a different situation from a week
  // with nothing on: name the reason and offer the other levels, rather than
  // sending the client to next week past classes that are sitting right there.
  const emptyStateProps = hiddenByLevel
    ? {
        title: `No ${activeLevel.sportName} classes at your level${isMulti(activeLevel) ? "s" : ""} this week`,
        description: `${hiddenByLevel} other ${hiddenByLevel === 1 ? "class is" : "classes are"} scheduled this week.`,
        actionLabel: "Show all levels",
        onAction: () => setLevelScope(null)
      }
    : {
        title: "No classes this week",
        description: "No classes are scheduled for the selected week and filters. Try the next week or adjust your filters.",
        actionLabel: "Next week",
        onAction: goNextWeek
      };

  // Reset transient UI when the underlying query changes.
  useEffect(() => {
    setActiveSheet(null);
    setOptionsAnchor(null);
    setOptionsClass(null);
  }, [weekStart, filters.sport, filters.serviceId, filters.trainerId, filters.locationId, filters.age, levelScope]);

  const handleJoin = (cls) => {
    addToCart("class", {
      type: "class",
      bookishId: cls._id,
      name: cls.name,
      date: cls.date,
      time: cls.time,
      duration: cls.duration,
      trainerId: cls.trainer?._id || null,
      trainerName: cls.trainer?.name || null,
      facilityId: cls.facility?._id || null,
      facilityName: cls.facility?.name || null,
      serviceId: cls.serviceId,
      pricingId: cls.pricingId,
      price: cls.price,
      currency,
      locationId: cls.location?._id || null,
      locationName: cls.location?.name || null
    });
    if (isAuthenticated) {
      navigate(`/${slug}/checkout`);
    } else {
      enqueueSnackbar("Please sign in to complete your booking", { variant: "warning" });
      navigate(`/${slug}/login?redirect=checkout`);
    }
  };

  // Session detail page — roster of signed-up players lives there.
  const handleOpenDetail = (cls) => navigate(`/${slug}/classes/${cls._id}`);

  // Class waitlist: a full session the client could take offers "Join waitlist"
  // instead of a dead Full button. Signed out, the list needs a client to
  // message, so it goes through login first. The popover/card closes itself.
  const handleJoinWaitlist = async (cls) => {
    if (joiningWaitlist.current.has(cls._id)) return;
    if (!isAuthenticated) {
      enqueueSnackbar("Please sign in to join the waitlist", { variant: "warning" });
      navigate(`/${slug}/login?redirect=${encodeURIComponent(`classes/${cls._id}`)}`);
      return;
    }
    joiningWaitlist.current.add(cls._id);
    try {
      const res = await api.joinClassWaitlist(slug, cls._id);
      enqueueSnackbar(joinedWaitlistMessage(res), { variant: "success" });
      patchWaitlist(cls._id, {
        isListed: true,
        position: res.position,
        count: res.count,
        entryId: res.entryId,
        isListFull: res.count >= res.maxSize
      });
    } catch (err) {
      enqueueSnackbar(err.message || "Could not join the waitlist", { variant: "error" });
    } finally {
      joiningWaitlist.current.delete(cls._id);
    }
  };

  const handleLeaveWaitlist = async (cls) => {
    try {
      await api.leaveClassWaitlist(slug, cls._id);
      enqueueSnackbar("You've left the waitlist", { variant: "info" });
      const count = Math.max(0, (cls.waitlist?.count || 1) - 1);
      patchWaitlist(cls._id, { isListed: false, position: null, entryId: null, offer: null, count, isListFull: false });
    } catch (err) {
      enqueueSnackbar(err.message || "Could not leave the waitlist", { variant: "error" });
    }
  };

  // A pack that undercuts the class's own price, when the client doesn't
  // already hold one. Buying happens on Plans, so the nudge deep-links there.
  // The offers arrive with the week they belong to, so a card never paints
  // before its hint.
  const findPackageOffer = usePackageOffers(timetable?.packageOffers);

  // Withheld from a class the client could not take: the pack would buy
  // sessions they still could not sit in, and would sit under the blocking
  // reason arguing with it.
  //
  // Only when we know who is looking, though. Signed out, eligibility is
  // decided against no client at all, so every level-gated class reads
  // "Assessment required" — to a first-time visitor and to a member of ten
  // years alike. That is a "not yet", not a "no", and both are exactly who the
  // offer is for.
  const packageOfferFor = (cls) => {
    if (cls.spotsAvailable === 0 || cls.isJoined) return null;
    if (isAuthenticated && cls.eligibility?.eligible === false) return null;
    return findPackageOffer(cls);
  };
  // joinAfter rides along so Plans can send the client back to this session
  // once the pack is paid for, instead of dropping them on the widget home.
  const handleViewPackage = (offer, cls) =>
    navigate(`/${slug}/plans?highlight=${encodeURIComponent(offerRowKey(offer))}&joinAfter=${encodeURIComponent(cls._id)}`);

  const handleSportChange = (sportId) => setFilters({ sport: sportId || null, serviceId: null });

  const closeSheet = () => setActiveSheet(null);
  const closeOptionsPopover = () => {
    setOptionsAnchor(null);
    setOptionsClass(null);
  };
  const handleClassChipClick = (e, cls) => {
    setOptionsClass(cls);
    setOptionsAnchor(e.currentTarget);
  };

  const todayISO = dateUtils.today();
  const canGoPrevWeek = weekStart > dateUtils.weekStart(new Date());
  const weekLabel = `${DateTime.fromISO(weekDates[0]).toFormat("MMM d")} – ${DateTime.fromISO(weekDates[6]).toFormat("MMM d, yyyy")}`;

  const selectedSport = availableSports.find((s) => s._id === filters.sport);
  const selectedService = sportFilteredServices.find((s) => s._id === filters.serviceId);
  const selectedTrainer = availableTrainers.find((t) => t._id === filters.trainerId);
  const selectedLocationObj = locations?.find((l) => l._id === filters.locationId);
  const selectedAge = availableAges.find((a) => a.value === filters.age);

  const sheetTitles = {
    sport: "Select Sport",
    service: "Select Class",
    age: "Select Age & Gender",
    trainer: "Select Trainer",
    location: "Select Location",
    level: "Select Level"
  };

  return {
    navigate,
    goBack,
    slug,
    currency,
    isMobile,
    isAuthenticated,
    filters,
    setFilter,
    setFilters,
    viewMode,
    setViewMode,
    weekDates,
    loading,
    error,
    timetable,
    ownedPackHorizon,
    ownedPackCount,
    ownedPackCapped,
    ownedPackStart,
    goToPackStart,
    pendingDay,
    clearPendingDay,
    classServices,
    availableSports,
    sportFilteredServices,
    serviceLocations,
    locationFilteredServices,
    availableTrainers,
    availableAges,
    levelFilterAvailable,
    levelOptions,
    levelScope,
    levelValue,
    levelChipLabel,
    setLevelScope,
    emptyStateProps,
    classesByDate,
    nonEmptyDays,
    weekHasClasses,
    dayViewActive,
    canApplyPreferences,
    applyPreferences,
    handleJoin,
    handleJoinWaitlist,
    handleLeaveWaitlist,
    handleOpenDetail,
    packageOfferFor,
    handleViewPackage,
    handleSportChange,
    activeSheet,
    setActiveSheet,
    closeSheet,
    sheetTitles,
    optionsAnchor,
    optionsClass,
    closeOptionsPopover,
    handleClassChipClick,
    goPrevWeek,
    goNextWeek,
    canGoPrevWeek,
    weekLabel,
    todayISO,
    selectedSport,
    selectedService,
    selectedTrainer,
    selectedLocationObj,
    selectedAge
  };
}
